import { useState } from "react";
import { Loader2, Sparkles, Users, Hash, Info } from "lucide-react";
import { createClassroom } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Create classroom modal — supports vanity codes (letters/numbers/dashes),
// 30-student cap for free teachers (unlimited for Pro), and classroom tone
// (kids / teen / college) that will later style the live-game modes.
export const CreateClassModal = ({ open, onClose, onCreated }) => {
  const { user } = useAuth();
  const isPro = user?.plan === "pro";

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [tone, setTone] = useState("teen");
  const [codeMode, setCodeMode] = useState("auto");   // "auto" | "custom"
  const [customCode, setCustomCode] = useState("");
  const [seatLimit, setSeatLimit] = useState(30);
  const [seatUnlimited, setSeatUnlimited] = useState(false);
  const [saving, setSaving] = useState(false);

  // Live-validate the vanity code so the submit button shows a hint.
  const codeHint = (() => {
    if (codeMode !== "custom") return null;
    const c = customCode.trim().toUpperCase();
    if (!c) return "Pick something memorable like MS-DOE-BIO-2026";
    if (c.length < 4 || c.length > 24) return "4-24 characters";
    if (!/^[A-Z0-9-]+$/.test(c)) return "Letters, numbers, dashes only";
    if (c.startsWith("-") || c.endsWith("-") || c.includes("--")) return "Dashes can't start, end, or repeat";
    return null;
  })();

  const reset = () => {
    setName(""); setSubject(""); setTone("teen");
    setCodeMode("auto"); setCustomCode("");
    setSeatLimit(30); setSeatUnlimited(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || saving) return;
    if (codeMode === "custom" && codeHint) {
      toast.error(codeHint);
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), subject: subject.trim(), tone };
      if (codeMode === "custom" && customCode.trim()) {
        payload.custom_code = customCode.trim().toUpperCase();
      }
      // Pro teachers get unlimited by default (seat_limit=0); free teachers always 30.
      if (isPro && seatUnlimited) payload.seat_limit = 0;
      else payload.seat_limit = isPro ? Math.max(1, parseInt(seatLimit) || 30) : 30;
      const c = await createClassroom(payload);
      onCreated(c);
      onClose();
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogTitle className="sr-only">Create a classroom</DialogTitle>
        <DialogDescription className="sr-only">Set up a new class for your students to join.</DialogDescription>
        <form onSubmit={submit}>
          <div className="p-6 pb-4">
            <h2 className="font-serif text-2xl font-bold">New classroom</h2>
            <p className="mt-1 text-sm text-muted-foreground">Students join with a code you'll share with them.</p>
          </div>

          <div className="px-6 pb-6 space-y-5 max-h-[70vh] overflow-y-auto">
            {/* Class name */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Class name</label>
              <input data-testid="new-class-name" value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="e.g. AP Biology · Period 4" required
                     className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60" />
            </div>

            {/* Subject */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Subject <span className="normal-case tracking-normal font-normal text-muted-foreground/60">(optional)</span>
              </label>
              <input data-testid="new-class-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
                     placeholder="Biology"
                     className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60" />
            </div>

            {/* Join code */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                <Hash className="h-3.5 w-3.5" /> Join code
              </label>
              <div className="mt-2 flex gap-1 rounded-full border border-border p-1 w-fit">
                <button type="button" data-testid="code-mode-auto"
                        onClick={() => setCodeMode("auto")}
                        className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${codeMode === "auto" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  Auto-generate
                </button>
                <button type="button" data-testid="code-mode-custom"
                        onClick={() => setCodeMode("custom")}
                        className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${codeMode === "custom" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  Pick your own
                </button>
              </div>
              {codeMode === "custom" && (
                <>
                  <input data-testid="new-class-custom-code" value={customCode}
                         onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                         placeholder="MS-DOE-BIO-2026"
                         maxLength={24}
                         className={`mt-2 w-full font-mono tracking-widest rounded-xl border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60 ${codeHint && customCode ? "border-destructive" : "border-input"}`} />
                  <p className={`mt-1.5 text-xs ${codeHint && customCode ? "text-destructive" : "text-muted-foreground"}`}>
                    {codeHint || "Letters, numbers, dashes. e.g. MS-DOE-BIO-2026"}
                  </p>
                </>
              )}
            </div>

            {/* Seat limit */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" /> Seat limit
              </label>
              {!isPro ? (
                <div className="mt-2 rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-3">
                  <p className="text-sm">
                    <span className="font-bold">30 students</span>
                    <span className="text-muted-foreground"> · free teacher plan</span>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">Upgrade to Pro for unlimited seats.</p>
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" data-testid="seat-unlimited"
                           checked={seatUnlimited}
                           onChange={(e) => setSeatUnlimited(e.target.checked)}
                           className="h-4 w-4 rounded accent-accent" />
                    Unlimited (Pro teachers)
                  </label>
                  {!seatUnlimited && (
                    <div className="flex items-center gap-3">
                      <input type="range" min="5" max="200" step="5"
                             value={seatLimit}
                             onChange={(e) => setSeatLimit(parseInt(e.target.value))}
                             className="flex-1 accent-accent" />
                      <span data-testid="seat-limit-value" className="font-mono text-sm font-bold w-14 text-right">{seatLimit}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Tone */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> Class tone
                <span className="normal-case tracking-normal font-normal text-muted-foreground/60">(styles live games)</span>
              </label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[
                  { id: "kids", label: "Kids", hint: "K-5 · bright + playful" },
                  { id: "teen", label: "Teen", hint: "6-12 · energetic" },
                  { id: "college", label: "College", hint: "Higher-ed · academic" },
                ].map((t) => (
                  <button key={t.id} type="button" data-testid={`tone-${t.id}`}
                          onClick={() => setTone(t.id)}
                          className={`rounded-xl border-2 px-3 py-2.5 text-left transition ${tone === t.id ? "border-accent bg-accent/10" : "border-border hover:border-accent/40"}`}>
                    <p className="text-sm font-bold">{t.label}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{t.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Info banner — only shows when picking something non-trivial */}
            {(codeMode === "custom" || tone !== "teen") && (
              <div className="rounded-xl bg-accent/5 border border-accent/20 px-3 py-2.5 flex gap-2">
                <Info className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  You can rename the code or change tone anytime from the classroom settings.
                </p>
              </div>
            )}

            <button data-testid="new-class-submit" type="submit"
                    disabled={saving || !name.trim() || (codeMode === "custom" && !!codeHint && customCode.length > 0)}
                    className="w-full rounded-xl bg-accent text-accent-foreground py-3.5 font-semibold disabled:opacity-60 hover:opacity-90 transition">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Create classroom"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
